import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { downloadBase64File } from "@/inventory/lib/download"
import {
  ensureDefaultCategoriesFn,
  exportSupplyItemsCsvFn,
  exportSupplyItemsExcelFn,
  exportSupplyItemsPdfSummaryFn,
  listSupplyCategoriesFn,
} from "@/supplies/rpc"
import { Button } from "@/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/ui/card"
import { Input } from "@/ui/input"
import { Label } from "@/ui/label"
import { cn } from "@/ui/utils"

export const Route = createFileRoute("/_supplies-layout/supplies/export")({
  component: ExportPage,
})

type ExportKind = "csv" | "excel" | "pdf"

interface FilterState {
  search: string
  categoryId: string
  lowStockOnly: boolean
  /** "active" | "inactive" | "all" */
  status: "active" | "inactive" | "all"
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== "") {
      next[key] = value
    }
  }
  return next as Partial<T>
}

function selectClassName() {
  return cn(
    "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] md:text-sm dark:bg-input/30",
    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
    "disabled:cursor-not-allowed disabled:opacity-50",
  )
}

function describeClientFilters(filters: FilterState): string {
  const parts: string[] = []
  if (filters.search.trim()) {
    parts.push(`search: ${filters.search.trim()}`)
  }
  if (filters.categoryId) {
    parts.push(`categoryId: ${filters.categoryId}`)
  }
  if (filters.lowStockOnly) {
    parts.push("low stock only")
  }
  if (filters.status === "active") {
    parts.push("active only")
  } else if (filters.status === "inactive") {
    parts.push("inactive only")
  } else {
    parts.push("all statuses")
  }
  return parts.join("; ") || "all items"
}

function ExportPage() {
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    categoryId: "",
    lowStockOnly: false,
    status: "active",
  })
  const [error, setError] = useState<string | null>(null)
  const [lastExport, setLastExport] = useState<ExportKind | null>(null)

  const seedQuery = useQuery({
    queryKey: ["supply-categories", "ensure-defaults"],
    queryFn: () => ensureDefaultCategoriesFn(),
  })

  const categoriesQuery = useQuery({
    queryKey: ["supply-categories", { includeInactive: true }],
    queryFn: () => listSupplyCategoriesFn({ data: { includeInactive: true } }),
    enabled: seedQuery.isSuccess,
  })

  const exportParams = useMemo(() => {
    const categoryId = filters.categoryId
      ? Number(filters.categoryId)
      : undefined
    return stripUndefined({
      search: filters.search.trim() || undefined,
      categoryId:
        categoryId != null && Number.isFinite(categoryId)
          ? categoryId
          : undefined,
      lowStockOnly: filters.lowStockOnly || undefined,
      // null = all statuses (must not use undefined — server defaults that to active-only)
      isActive: filters.status === "all" ? null : filters.status === "active",
      filtersDescription: describeClientFilters(filters),
    })
  }, [filters])

  const exportMutation = useMutation({
    mutationFn: async (kind: ExportKind) => {
      const data = exportParams
      if (kind === "csv") {
        return { kind, payload: await exportSupplyItemsCsvFn({ data }) }
      }
      if (kind === "excel") {
        return { kind, payload: await exportSupplyItemsExcelFn({ data }) }
      }
      return { kind, payload: await exportSupplyItemsPdfSummaryFn({ data }) }
    },
    onMutate: (kind) => {
      setError(null)
      setLastExport(kind)
    },
    onSuccess: ({ payload }) => {
      downloadBase64File(payload)
    },
    onError: (err) => {
      setError(getErrorMessage(err))
    },
  })

  const isExporting = exportMutation.isPending
  const activeKind = isExporting ? lastExport : null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Export</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Filter supply items, then download CSV, Excel, or a PDF summary.
        </p>
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="grid gap-1.5">
            <Label htmlFor="export-search">Search</Label>
            <Input
              id="export-search"
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  search: event.target.value,
                }))
              }
              placeholder="Name or SKU"
              autoComplete="off"
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="export-category">Category</Label>
            <select
              id="export-category"
              value={filters.categoryId}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  categoryId: event.target.value,
                }))
              }
              className={selectClassName()}
            >
              <option value="">All categories</option>
              {(categoriesQuery.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                  {!category.isActive ? " (inactive)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="export-status">Status</Label>
            <select
              id="export-status"
              value={filters.status}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  status: event.target.value as FilterState["status"],
                }))
              }
              className={selectClassName()}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={filters.lowStockOnly}
                onChange={(event) =>
                  setFilters((prev) => ({
                    ...prev,
                    lowStockOnly: event.target.checked,
                  }))
                }
                className="size-4 rounded border border-input"
              />
              Low stock only
            </label>
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">CSV</CardTitle>
            <CardDescription>
              Spreadsheet-friendly item rows for filtered supplies.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("csv")}
            >
              {activeKind === "csv" ? "Downloading…" : "Download CSV"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Excel</CardTitle>
            <CardDescription>
              .xlsx workbook with the same filtered item data.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("excel")}
            >
              {activeKind === "excel" ? "Downloading…" : "Download Excel"}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">PDF summary</CardTitle>
            <CardDescription>
              Compact PDF summary of matching items and applied filters.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              type="button"
              disabled={isExporting}
              onClick={() => exportMutation.mutate("pdf")}
            >
              {activeKind === "pdf" ? "Downloading…" : "Download PDF"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
